import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, TrendingUp, Target, Users, BarChart3, MapPin, Clock, Award } from "lucide-react";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { EnhancedStructuredData } from "@/components/EnhancedStructuredData";
import { SEOArticleStructure } from "@/components/SEOArticleStructure";
import { InternalLinks } from "@/components/InternalLinks";
import { useSEOOptimization } from "@/hooks/useSEOOptimization";
import { generateCanonicalUrl } from "@/utils/seoHelpers";
import { getLocationRelatedLinks } from "@/utils/internalLinksConfig";

const SEOTheWoodlandsPage = () => {
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const toggleLanguage = () => setLanguage(language === 'es' ? 'en' : 'es');
  
  useSEOOptimization();
  const canonicalUrl = generateCanonicalUrl('/seo-the-woodlands');

  const services = [
    {
      icon: Target,
      title: "Auditoría Técnica",
      description: "Velocidad, indexación, Core Web Vitals y arquitectura optimizada."
    },
    {
      icon: MapPin,
      title: "SEO Local",
      description: "Google Business Profile, citaciones NAP, reseñas y landing pages por ciudad/servicio."
    },
    {
      icon: BarChart3,
      title: "Contenido que Posiciona",
      description: "Clusters temáticos, guías y FAQs alineados a intención (incluyendo SGE/IA)."
    },
    {
      icon: TrendingUp,
      title: "Link Building Local",
      description: "PR digital en Texas, Houston, Conroe y Spring para aumentar tu autoridad."
    },
    {
      icon: Award,
      title: "Analytics & ROI",
      description: "GA4 + GSC + Call/Form tracking y panel mensual de resultados."
    },
    {
      icon: Users,
      title: "On-page & Entidades",
      description: "Títulos, esquema, interlinking y E-E-A-T para máxima relevancia."
    }
  ];

  const process = [
    {
      day: "Día 0-7",
      title: "Auditoría + Plan 90",
      description: "Identificamos prioridades, definimos KPIs y creamos el roadmap."
    },
    {
      day: "Día 8-30",
      title: "Fundamentos",
      description: "Implementamos técnico, GBP, páginas locales y tracking completo."
    },
    {
      day: "Día 31-60",
      title: "Contenido & Enlaces",
      description: "Desarrollamos clusters, FAQs, citaciones y PR local."
    },
    {
      day: "Día 61-90",
      title: "Optimización y Escalado",
      description: "Tests on-page, CRO y expansión a nuevas ubicaciones/servicios."
    }
  ];

  const results = [
    "Mayor visibilidad local en map pack y resultados orgánicos",
    "Más leads cualificados vía formulario y llamadas",
    "Menor CPA al combinar SEO + PPC de forma estratégica",
    "Crecimiento compuesto con contenido evergreen y autoridad temática"
  ];

  const sectors = [
    "Home Services",
    "Salud y Bienestar",
    "Legal",
    "Educación",
    "Real Estate",
    "Restaurantes",
    "Profesionales B2B"
  ];

  const plans = [
    {
      name: "Kickstart Local",
      duration: "90 días",
      description: "Auditoría, GBP, páginas locales y tracking completo para empezar con base sólida.",
      features: ["Auditoría técnica", "Optimización GBP", "3 páginas locales", "Setup tracking"]
    },
    {
      name: "Growth SEO",
      duration: "Mensual",
      description: "Contenido mensual + link building local + CRO para crecimiento sostenido.",
      features: ["4 artículos/mes", "Link building", "CRO mensual", "Reportes detallados"]
    },
    {
      name: "Performance Suite",
      duration: "Mensual",
      description: "SEO + PPC integrados para acelerar demanda con reporting unificado.",
      features: ["Todo Growth SEO", "Gestión Google Ads", "Panel unificado", "Optimización continua"]
    }
  ];

  const faqs = [
    {
      question: "¿En cuánto tiempo veré resultados de SEO?",
      answer: "Generalmente entre 8 y 12 semanas para mejoras iniciales (map pack y páginas locales). El crecimiento orgánico sostenido se consolida a 3–6 meses."
    },
    {
      question: "¿Cuál es la diferencia entre SEO local y SEO nacional?",
      answer: "El SEO local prioriza mapas, reseñas y señales geográficas; el nacional se centra en autoridad temática amplia y cobertura por intención."
    },
    {
      question: "¿Pueden gestionar Google Ads junto al SEO?",
      answer: "Sí. Integramos SEO + PPC para acelerar captación y aprender de datos de búsqueda pagada."
    },
    {
      question: "¿Cómo reportan el ROI?",
      answer: "Panel mensual con rankings, tráfico, leads por canal, llamadas/grabaciones y embudo completo."
    }
  ];

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>Agencia SEO en The Woodlands | Posicionamiento Local y Leads – Luis ROC</title>
        <meta 
          name="description" 
          content="Mejora tu visibilidad en Google con SEO Local en The Woodlands. Auditamos, optimizamos y generamos leads medibles. Solicita tu reunión inicial gratuita con Luis ROC." 
        />
        <meta name="keywords" content="agencia seo the woodlands, seo the woodlands, seo local the woodlands, google business profile, posicionamiento google the woodlands, consultor seo the woodlands" />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content="Agencia SEO en The Woodlands | Luis ROC" />
        <meta property="og:description" content="SEO Local en The Woodlands que convierte búsquedas en clientes. Reunión inicial gratuita." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="es_US" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Agencia SEO en The Woodlands | Luis ROC" />
        <meta name="twitter:description" content="SEO Local que genera leads medibles en The Woodlands." />
      </Helmet>

      <EnhancedStructuredData
        service={{
          name: "Agencia SEO The Woodlands",
          description: "Servicios de SEO local y posicionamiento en Google para empresas de The Woodlands, Texas",
          provider: "Luis ROC",
          areaServed: ["The Woodlands", "Houston", "Spring", "Conroe", "Montgomery County"],
        }}
        faqs={faqs}
        webpage={{
          name: "Agencia SEO en The Woodlands",
          description: "Servicios profesionales de SEO local en The Woodlands, TX. Auditorías, optimización y generación de leads.",
          url: canonicalUrl,
          keywords: ["agencia seo the woodlands", "seo local", "google business profile", "posicionamiento google"]
        }}
      />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={toggleLanguage} />
        
        <main>
          <SEOArticleStructure
            title="Agencia SEO en The Woodlands que convierte búsquedas en clientes"
            description="Servicios profesionales de SEO local en The Woodlands. Auditamos, optimizamos y generamos leads medibles."
            author="Luis ROC"
            keywords={["agencia seo the woodlands", "seo local", "google business profile"]}
            className="container mx-auto px-4 py-8"
          >
            {/* Breadcrumb Navigation */}
            <BreadcrumbNav 
              items={[
                { name: "Servicios", url: "/servicios" },
                { name: "SEO The Woodlands", url: "/seo-the-woodlands" }
              ]}
              language="es"
            />

            {/* Hero Section */}
            <section className="py-16 text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                Agencia SEO en The Woodlands que convierte búsquedas en clientes
              </h1>
              
              <div className="max-w-4xl mx-auto mb-8">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  En Luis ROC ayudamos a empresas de The Woodlands y alrededores a aparecer en Google y convertir ese tráfico en leads y ventas reales. 
                  Combinamos SEO técnico, contenido estratégico y SEO local (Map Pack + Google Business Profile) con tracking de conversiones, 
                  para que veas el impacto en tus resultados. Nuestro enfoque es medible, transparente y orientado a ROI: auditamos, priorizamos 
                  quick wins y construimos crecimiento sostenible. Si buscas un partner que hable tu idioma (EN/ES) y que rinda cuentas con datos, 
                  empecemos hoy.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  onClick={() => window.location.href = '/diagnostico-seo-gratis'}
                  className="text-lg px-8"
                >
                  Agenda tu reunión inicial gratuita de 30 minutos
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => window.location.href = '/contacto'}
                  className="text-lg px-8"
                >
                  Agenda una Consulta
                </Button>
              </div>
            </section>

            {/* Services Section */}
            <section className="py-16 bg-accent/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                  Qué incluye nuestro SEO en The Woodlands
                </h2>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {services.map((service, index) => {
                    const Icon = service.icon;
                    return (
                      <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                        <Icon className="h-12 w-12 text-primary mb-4" />
                        <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                        <p className="text-muted-foreground">{service.description}</p>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Process Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                Cómo trabajamos (proceso en 90 días)
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                Metodología probada para resultados sostenibles con entregables mensuales
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {process.map((step, index) => (
                  <Card key={index} className="p-6 relative">
                    <div className="absolute -top-4 left-6 bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div className="flex items-center gap-2 mb-3 mt-2">
                      <Clock className="h-5 w-5 text-primary" />
                      <span className="font-semibold text-primary">{step.day}</span>
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </Card>
                ))}
              </div>
            </section>

            {/* Results Section */}
            <section className="py-16 bg-primary/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                  Resultados que puedes esperar
                </h2>
                
                <div className="max-w-3xl mx-auto space-y-4">
                  {results.map((result, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-background rounded-lg">
                      <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-lg">{result}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Sectors Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                Sectores donde brillamos en The Woodlands
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                Creamos páginas y pruebas sociales por vertical para acortar el ciclo de decisión
              </p>
              
              <div className="flex flex-wrap justify-center gap-4">
                {sectors.map((sector, index) => (
                  <div 
                    key={index}
                    className="px-6 py-3 bg-accent text-accent-foreground rounded-full font-medium hover:bg-accent/80 transition-colors"
                  >
                    {sector}
                  </div>
                ))}
              </div>
            </section>

            {/* Plans Section */}
            <section className="py-16 bg-accent/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                  Planes y engagement
                </h2>
                <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                  Solicita una propuesta a medida según tus objetivos y mercado
                </p>
                
                <div className="grid md:grid-cols-3 gap-8">
                  {plans.map((plan, index) => (
                    <Card key={index} className="p-8 hover:shadow-xl transition-shadow">
                      <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                      <p className="text-primary font-semibold mb-4">{plan.duration}</p>
                      <p className="text-muted-foreground mb-6">{plan.description}</p>
                      
                      <ul className="space-y-3 mb-6">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <Button 
                        className="w-full"
                        onClick={() => window.location.href = '/contacto'}
                      >
                        Solicitar Propuesta
                      </Button>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Preguntas Frecuentes
              </h2>
              
              <div className="max-w-3xl mx-auto space-y-6">
                {faqs.map((faq, index) => (
                  <Card key={index} className="p-6">
                    <h3 className="text-xl font-semibold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </Card>
                ))}
              </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Comienza a dominar Google en The Woodlands
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
                Revisamos el contexto de tu sitio y definimos juntos los posibles siguientes pasos
              </p>
              <Button 
                size="lg"
                variant="secondary"
                onClick={() => window.location.href = '/diagnostico-seo-gratis'}
                className="text-lg px-8"
              >
                Agenda tu reunión inicial gratuita de 30 minutos
              </Button>
            </section>

            {/* Internal Links Section */}
            <InternalLinks 
              links={getLocationRelatedLinks('the-woodlands', 'es')}
              title="Servicios Relacionados y Otras Ubicaciones"
              language="es"
            />
          </SEOArticleStructure>
        </main>

        <KnowledgeConnections path="/seo-the-woodlands" language={language} />

        <Footer language={language} />
      </div>
    </>
  );
};

export default SEOTheWoodlandsPage;
